import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasControlledAccessSecurity,
  useRentalManagerUserPropertyEditWebApiHandler_hasDoormanSecurity,
  useRentalManagerUserPropertyEditWebApiHandler_hasGatedEntrySecurity,
  useRentalManagerUserPropertyEditWebApiHandler_hasIntercomSecurity,
  useRentalManagerUserPropertyEditWebApiHandler_hasNightPatrolSecurity,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { SECURITY_KEYS, SECURITY_METADATA, type SecurityKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<SecurityKey, boolean>;

const SecurityAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const securityTypes = listingDetails?.buildingAttributes?.securityTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasControlledAccess: securityTypes?.hasControlledAccess ?? false,
    hasDoorman: securityTypes?.hasDoorman ?? false,
    hasGatedEntry: securityTypes?.hasGatedEntry ?? false,
    hasIntercom: securityTypes?.hasIntercom ?? false,
    hasNightPatrol: securityTypes?.hasNightPatrol ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Security updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateControlledAccess } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasControlledAccessSecurity,
    skipToast: true,
    meta: { context: { api: 'hasControlledAccessSecurity', details: { listingAlias } } },
  });

  const { mutateAsync: updateDoorman } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDoormanSecurity,
    skipToast: true,
    meta: { context: { api: 'hasDoormanSecurity', details: { listingAlias } } },
  });

  const { mutateAsync: updateGatedEntry } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGatedEntrySecurity,
    skipToast: true,
    meta: { context: { api: 'hasGatedEntrySecurity', details: { listingAlias } } },
  });

  const { mutateAsync: updateIntercom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasIntercomSecurity,
    skipToast: true,
    meta: { context: { api: 'hasIntercomSecurity', details: { listingAlias } } },
  });

  const { mutateAsync: updateNightPatrol } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasNightPatrolSecurity,
    skipToast: true,
    meta: { context: { api: 'hasNightPatrolSecurity', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateControlledAccess> = {
    hasControlledAccess: updateControlledAccess,
    hasDoorman: updateDoorman,
    hasGatedEntry: updateGatedEntry,
    hasIntercom: updateIntercom,
    hasNightPatrol: updateNightPatrol,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as SecurityKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = SECURITY_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Security</Legend>} disabled={isInputFormDisabled}>
      {SECURITY_KEYS.map((fieldName) => {
        const metadata = SECURITY_METADATA[fieldName];
        return (
          <LabeledControl
            key={`securityTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(SecurityAmenities);
